import { RefObject, useEffect } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export const getFocusableElements = (container: HTMLElement) =>
  Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
  ).filter(
    element =>
      // Skips elements hidden with display: none, which have no boxes
      element.getClientRects().length > 0 &&
      !element.closest('[aria-hidden="true"]'),
  );

/**
 * Keeps keyboard focus inside the container while active, by wrapping Tab from
 * the last focusable element to the first one and Shift+Tab the other way.
 */
const useFocusTrap = (
  containerRef: RefObject<HTMLElement | null>,
  isActive: boolean,
) => {
  useEffect(() => {
    if (!isActive) return;

    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || !containerRef.current) return;

      const focusableElements = getFocusableElements(containerRef.current);
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (!firstElement || !lastElement) return;

      const { activeElement } = document;

      // A modal opened on top, e.g. from a link in the menu, makes the rest of
      // the page inert and keeps focus itself, so its Tab is left alone
      const openDialog = activeElement?.closest('dialog[open]');

      if (openDialog && !openDialog.contains(containerRef.current)) return;

      const isOutside = !containerRef.current.contains(activeElement);

      if (event.shiftKey && (activeElement === firstElement || isOutside)) {
        event.preventDefault();
        lastElement.focus();
      } else if (
        !event.shiftKey &&
        (activeElement === lastElement || isOutside)
      ) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    window.addEventListener('keydown', handleKeydown);

    return () => {
      window.removeEventListener('keydown', handleKeydown);
    };
  }, [containerRef, isActive]);
};

export default useFocusTrap;
